import { TeContainer, TeLinearLoader, TeModal } from '@topcon/tuicl-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  RequestBusinessPartnerFormData,
  useBusinessPartnerForm,
} from '../../hooks/forms/useBusinessPartnerForm';
import { BusinessPartnerForm } from '../forms/BusinessPartnerForm';
import {
  BusinessPartnerBody,
  RequestBusinessPartnerEndCustomerTypeEnum,
  ServiceAgreementTermsEnum,
} from '../../api/generated/pm';
import {
  AvailableAgreementsFilters,
  useAvailableAgreements,
} from '../../pages/Settings/Dealers/dealers.queries';
import SelectDealerStep from '../../pages/Settings/Dealers/SelectDealerStep';
import SelectAgreementTypeStep from '../../pages/Settings/Dealers/SelectAgreementTypeStep';
import ReviewAgreementStep from '../ServiceAgreement/Customer/ReviewAgreementStep';
import OrgCard from '../OrgCard';
import { formatAddress } from '../../utils/formatAddress';
import CompanyDivisionModalActions from './CompanyDivisionModalActions';
import useDebounce from '../../hooks/useDebounce';
interface Props {
  open: boolean;
  isDivision?: boolean;
  onClose: (shouldUpdate: boolean) => void;
  /** Seeds the General Information step's initial field values (e.g. name/address already known from an external source). */
  defaultValues?: Partial<RequestBusinessPartnerFormData>;
  /** Renders a dismissible "please verify" banner on the General Information step. Defaults to false. */
  showVerifyBanner?: boolean;
  /** Renders the General Information step's address fields as always editable, skipping the manual-input toggle. Defaults to false. */
  alwaysEditableAddress?: boolean;
  /**
   * The id of the Migration Candidate Dealer — the dealer that serviced this
   * candidate's organization in the source system (see `GLOSSARY.md`).
   * Authoritative on its own: when present, `SelectDealerStep` is skipped and
   * `providerBusinessPartnerId` is seeded from it, regardless of whether a
   * display name for it ever resolves.
   */
  migrationCandidateDealerId?: string;
  /**
   * The id of the Migration Candidate itself, set on the built request body
   * as `migrationCandidateBusinessPartnerId` so the backend can link the new
   * request back to the candidate and transition its status. Omitted for
   * ordinary (non-migration) create flows.
   */
  migrationCandidateBusinessPartnerId?: string;
}

export enum BusinessPartnerCreationStepEnum {
  BusinessPartnerForm = 1,
  SelectDealer = 2,
  SelectAgreement = 3,
  EulaConsent = 4,
}

function CompanyDivisionModal({
  isDivision,
  open,
  onClose,
  defaultValues,
  showVerifyBanner,
  alwaysEditableAddress,
  migrationCandidateDealerId,
  migrationCandidateBusinessPartnerId,
}: Props) {
  const { t } = useTranslation(['companies', 'divisions', 'common']);
  const [step, setStep] = useState<BusinessPartnerCreationStepEnum>(
    BusinessPartnerCreationStepEnum.BusinessPartnerForm
  );
  const [verifyBannerDismissed, setVerifyBannerDismissed] = useState<boolean>(false);

  const [businessPartner, setBusinessPartner] = useState<BusinessPartnerBody | undefined>(
    undefined
  );
  const [eulaConsent, setEulaConsent] = useState<boolean>(false);
  const [isAgreementAcknowledged, setIsAgreementAcknowledged] = useState<boolean>(false);
  const [filters, setFilters] = useState<AvailableAgreementsFilters>({});
  const debouncedFilters = useDebounce<AvailableAgreementsFilters>(filters);
  const { data: availableProviders, isLoading } = useAvailableAgreements(debouncedFilters);

  const skipDealerSelection = !!migrationCandidateDealerId;
  const steps = useMemo(
    () =>
      skipDealerSelection
        ? [
            BusinessPartnerCreationStepEnum.BusinessPartnerForm,
            BusinessPartnerCreationStepEnum.SelectAgreement,
            BusinessPartnerCreationStepEnum.EulaConsent,
          ]
        : [
            BusinessPartnerCreationStepEnum.BusinessPartnerForm,
            BusinessPartnerCreationStepEnum.SelectDealer,
            BusinessPartnerCreationStepEnum.SelectAgreement,
            BusinessPartnerCreationStepEnum.EulaConsent,
          ],
    [skipDealerSelection]
  );
  const activeStepId = steps[step - 1] ?? steps[0];

  const businessPartnerType = isDivision ? 'division' : 'company';
  const modalTitle = isDivision
    ? t('createDivision', { ns: 'divisions' })
    : t('createCompany', { ns: 'companies', keyPrefix: 'list' });

  const {
    formName,
    isPending,
    handleFormSubmit,
    setFormFields,
    validateForm,
    formFields,
    clearFields,
  } = useBusinessPartnerForm({
    businessPartnerType,
    defaultValues: {
      country: 'US',
      serviceAgreementTerms: ServiceAgreementTermsEnum.FULL_SERVICE,
      type: RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_INDIVIDUAL,
      ...defaultValues,
      // The Migration Candidate Dealer's id is authoritative: seed it straight
      // into the form rather than waiting on any lookup to resolve.
      ...(migrationCandidateDealerId && {
        providerBusinessPartnerId: migrationCandidateDealerId,
      }),
    },
    migrationCandidateBusinessPartnerId,
  });

  const formValid = validateForm(formFields);
  const totalSteps = steps.length;

  const handleClose = () => {
    if (isPending) return;
    clearFields();
    setStep(1);
    setVerifyBannerDismissed(false);
    onClose(false);
  };

  const handleNext = () => {
    if (step < totalSteps) setStep((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (step > 1) setStep((prev) => prev - 1);
  };

  const handleCreate = async () => {
    await handleFormSubmit();
    setStep(1);
    setVerifyBannerDismissed(false);
    onClose(true);
  };

  const canProceed = () => {
    if (activeStepId === BusinessPartnerCreationStepEnum.BusinessPartnerForm) return formValid;
    if (activeStepId === BusinessPartnerCreationStepEnum.SelectDealer)
      return formFields.providerBusinessPartnerId;
    if (activeStepId === BusinessPartnerCreationStepEnum.SelectAgreement)
      return isAgreementAcknowledged;
    if (activeStepId === BusinessPartnerCreationStepEnum.EulaConsent) return eulaConsent;
    return true;
  };

  const handleFiltersChange = (update: Partial<AvailableAgreementsFilters>) => {
    setFilters((prev) => ({ ...prev, ...update }));
  };

  const dealers = availableProviders?.items as BusinessPartnerBody[] | undefined;
  const footerText = t('step.count', { ns: 'common', current: step, total: totalSteps });

  /**
   * Display-only resolution of the Migration Candidate Dealer's name/address,
   * from the same `/v1/providers` list `SelectDealerStep` picks from —
   * fetched unconditionally above, so this costs no extra request and a
   * resolved dealer is provably one the user could legally have chosen.
   *
   * Not resolved via `GET /v1/businessPartners?filter[id][eq]=…`: that
   * endpoint returns `200` with `items: []` for a no-BP identity (confirmed
   * manually against QA), so a single-row lookup by id is not viable for
   * this caller today. Revisit if that scoping ever changes, or once the
   * backend embeds the dealer on the candidate directly.
   */
  const migrationCandidateDealer = useMemo(
    () => dealers?.find((dealer) => dealer.id === migrationCandidateDealerId),
    [dealers, migrationCandidateDealerId]
  );

  return (
    <TeModal
      variant="dark"
      open={open}
      closeMode="button"
      teclose={handleClose}
      modalTitle={modalTitle}
      width={'l'}
      contentPadding={0}
      closeable={!isPending}
      footerText={footerText}
      data-testid={`company-division-modal-${isDivision ? 'division' : 'company'}`}
    >
      <TeContainer
        column
        grow
        backgroundColor="background.primary"
        gap="xs"
        alignItems="stretch"
        padding="s"
      >
        {activeStepId === BusinessPartnerCreationStepEnum.BusinessPartnerForm && (
          <BusinessPartnerForm
            businessPartnerType={businessPartnerType}
            formFields={formFields}
            setFormFields={setFormFields}
            handleFormSubmit={handleFormSubmit}
            isPending={isPending}
            formName={formName}
            resetFieldsStatus={!open}
            useLocationSuggestion
            // `alwaysEditableAddress` is only ever set by callers seeding this
            // modal from a source system's own address (currently the
            // Migration Candidate flow) — exactly the case geolocation
            // auto-detect must stay off for, since that address already
            // carries a country.
            detectCountry={!alwaysEditableAddress}
            showVerifyBanner={showVerifyBanner && !verifyBannerDismissed}
            onDismissVerifyBanner={() => setVerifyBannerDismissed(true)}
            alwaysEditableAddress={alwaysEditableAddress}
          />
        )}

        {activeStepId === BusinessPartnerCreationStepEnum.SelectDealer && (
          <SelectDealerStep
            filters={filters}
            onFiltersChange={handleFiltersChange}
            providers={dealers}
            loading={isLoading}
            selectedDealer={businessPartner}
            onSelect={(bp) => {
              setBusinessPartner(bp);
              setFormFields((prev) => ({ ...prev, providerBusinessPartnerId: bp.id }));
            }}
          />
        )}

        {activeStepId === BusinessPartnerCreationStepEnum.SelectAgreement && (
          <>
            {migrationCandidateDealer && (
              <OrgCard
                name={migrationCandidateDealer.name}
                tagText={t('dealers.createModal.dealerOrganization', { ns: 'common' })}
                address={formatAddress(migrationCandidateDealer.companyAddress) || undefined}
                baseBackground="primary"
                addressFill
                testId="preselected-dealer-card"
              />
            )}
            <SelectAgreementTypeStep
              setType={(type) =>
                setFormFields((prev) => ({ ...prev, serviceAgreementTerms: type }))
              }
              type={formFields.serviceAgreementTerms}
              onAcknowledgedChange={setIsAgreementAcknowledged}
            />
          </>
        )}

        {activeStepId === BusinessPartnerCreationStepEnum.EulaConsent && (
          <ReviewAgreementStep onChecked={(value) => setEulaConsent(value)} />
        )}
      </TeContainer>
      <CompanyDivisionModalActions
        onClose={onClose}
        isPending={isPending}
        currentStep={step}
        onNextStep={handleNext}
        isNextStepDisabled={!canProceed()}
        totalSteps={totalSteps}
        onSubmit={handleCreate}
        onPreviousStep={handlePrevious}
        isDivision={isDivision}
      />
      {isPending && <TeLinearLoader rectangle slot="loader" />}
    </TeModal>
  );
}

export default CompanyDivisionModal;
